import { AloftPrice } from '@aloft/toolkit-shared';
import { Block, Typography } from '@aloft/toolkit-sui';

import { get } from 'lodash';
import { Fragment } from 'react';

import { AdjustmentTooltip } from '../AdjustmentTooltip.js';
import { usePairedCostAdjustmentSummaryModalContext } from './BreakdownDetailModal/usePairedCostAdjustmentSummaryModalContext.js';
import { TdGap } from './TdGap.js';
import { FeatureAndAdjustmentRow, PairedCostAdjustmentSummaryProps } from './types.js';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.js';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.js';
import { hasAdjustments } from './utils/hasAdjustments.js';

export const PairedCostAdjustmentSummaryTableRow = ({
  compSequence,
  subject,
  comps,
  row,
  includeAdjustmentCell = true,
  rowSpan = 1,
  isSimplifiedMode: showSummaryColumn,
  adjustmentLabels,
}: PairedCostAdjustmentSummaryProps & {
  row: FeatureAndAdjustmentRow;
  includeAdjustmentCell?: boolean;
  rowSpan?: number;
}) => {
  const { setPairedCostModal } = usePairedCostAdjustmentSummaryModalContext();

  return row.alwaysShow || hasAdjustments(comps, row.path) ? (
    <tr>
      <th className="data-cell">
        <Block className="data-cell-position-wrapper">
          <Typography variant="body1" color="textPrimary">
            {(get(adjustmentLabels, row.path) as string | undefined) ?? ''}
          </Typography>
        </Block>
      </th>
      <th className="data-cell">
        <Block className="data-cell-position-wrapper">
          <Typography variant="body1" color="textPrimary">
            {(get(subject, `${row.path}.value`) as string | undefined) ?? ''}
          </Typography>
        </Block>
      </th>
      {showSummaryColumn && (
        <th className="data-cell">
          <Block className="data-cell-position-wrapper">
            <Typography variant="body1" color="textPrimary">
              {(get(subject, `${row.path}.summary`) as string | undefined) ?? ''}
            </Typography>
          </Block>
        </th>
      )}
      {compSequence.map((compID, idx) => {
        const adjustment = get(comps, `${compID}.${row.path}.adjustment`) as unknown as
          | AloftPrice
          | undefined;
        const hasAdjustment = adjustment !== undefined && !adjustment.equals(AloftPrice.zero);

        const tooltip = get(comps, `${compID}.${row.path}.tooltip`) as unknown as
          | {
              calculations?: string[];
              description?: string;
            }
          | undefined;

        return (
          <Fragment key={`column-title-${compID}`}>
            <TdGap isSticky={idx === 0} />
            <td className="data-cell">
              <Block className="data-cell-position-wrapper">
                <Typography variant="body1" color="textPrimary">
                  {(get(comps, `${compID}.${row.path}.value`) as unknown as string | undefined) ??
                    ''}
                </Typography>
              </Block>
            </td>
            {includeAdjustmentCell && (
              <td
                className={`data-cell right-align${
                  row.suppressDetails || !hasAdjustment ? '' : ' adjustment-with-details'
                }`}
                rowSpan={rowSpan}
                onClick={
                  row.suppressDetails || !hasAdjustment
                    ? undefined
                    : () => setPairedCostModal(row.path)
                }
              >
                <Block className="data-cell-position-wrapper">
                  <Typography variant="body1" color={getColorFromAdjustment(adjustment)}>
                    {getStringValueFromAdjustment(adjustment)}
                  </Typography>
                </Block>
                <AdjustmentTooltip {...tooltip} />
              </td>
            )}
          </Fragment>
        );
      })}
    </tr>
  ) : null;
};
